import React from 'react';
import { FormattedMessage } from 'react-intl';
import Button from '@alltrails/shared/denali/components/Button';
import RadioGroup from '@alltrails/shared/denali/components/RadioGroup';
import CustomProvider from 'components/CustomProvider';
import { StringConstantsMixin } from '../mixins/shared/string_constants_mixin';
import { LanguageSupportUtil } from '../../../../utils/language_support_util';
import { SliderGroup } from '../form/slider_group';
import { FileUpload } from '../form/file_upload';
import { DragAndDrop } from '../form/drag_and_drop';
import { SectionDivider } from '../form/section_divider';
import { TextInput } from '../form/text_input';
import { Select } from '../form/select';
import Switch from '@alltrails/shared/denali/components/Switch';
import { Collapsible } from '../form/collapsible';
import { CheckboxGroup } from '../form/checkbox_group';
import * as styles from './styles.module.scss';

class Converter extends React.Component {
  constructor(props) {
    super(props);

    this.props.routeOptions.forEach(routeOption => {
      if (typeof routeOption.enabled === 'undefined') {
        routeOption.enabled = false;
      }
    });

    this.initialState = {
      convertTo: 'route',
      downloadAs: 'csv',
      submitting: false,
      uploadProgress: 0,
      uploadComplete: false,
      fileSizeLimit: 8e6,
      errorMessage: '',
      fileTypes: [
        {
          display: StringConstantsMixin.strings.CONVERTER.FILE_TYPES.ROUTE,
          value: 'route',
          options: this.props.fileTypes.route
        },
        {
          display: StringConstantsMixin.strings.CONVERTER.FILE_TYPES.WAYPOINTS,
          value: 'waypoint',
          options: this.props.fileTypes.waypoint
        }
      ],
      files: {
        uploadedPath: this.props.filePath,
        key: 0,
        fileName: '',
        fileSize: 0,
        fileUrl: '',
        isAttached: false,
        attachedFile: null,
        redirectSlug: this.props.convertedSlug,
        mapType: this.props.mapType
      },
      routeOptions: {
        expanded: false,
        options: this.props.routeOptions
      },
      routeSimplification: {
        expanded: false,
        selectedOption: 0,
        value: this.props.routeSimplificationMax,
        maxTrackPoints: false
      },
      cueSheets: {
        expanded: false,
        on: false,
        number: this.props.cueSheetOptions.number.value,
        distance: this.props.cueSheetOptions.distance.range
      }
    };
    this.state = this.initialState;
  }

  // This updates the nested state of a state var, and runs 'formreset' to clear validation messages on update.
  stateHandler(section, key) {
    return e => {
      this.formReset();
      const { value } = e.target;
      this.setState(state => {
        state[section][key] = value;
      });
    };
  }

  resetField(section, key) {
    return () => {
      this.formReset();
      this.setState(state => {
        state[section][key] = '';
      });
    };
  }

  resetPage() {
    return () => {
      this.setState(this.initialState);
    };
  }

  handleRemove() {
    return e => {
      this.setState({
        uploadProgress: 0,
        files: {
          key: this.state.files.key++,
          isAttached: false,
          attachedFile: '',
          fileName: '',
          fileSize: ''
        }
      });
    };
  }

  addFile(newFile) {
    this.setState(state => {
      state.uploadProgress = 0;
      state.files = {
        key: state.files.key++,
        isAttached: true,
        attachedFile: newFile,
        fileName: newFile.name,
        fileSize: newFile.size,
        fileUrl: ''
      };
    });
  }

  // These two functions are pretty similar. There's probably a better way to do this.
  handleFileFieldChange() {
    return e => {
      this.formReset();
      const newFile = e.target.files[0];
      this.addFile(newFile);
    };
  }

  handleDrop() {
    return e => {
      this.formReset();
      const newFile = e[0];
      this.addFile(newFile);
    };
  }

  sendData(data) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.upload.onprogress = e => {
        const done = e.position || e.loaded;
        const total = e.totalsize || e.total;
        const perc = Math.floor((done / total) * 1000) / 10;
        if (perc >= 100) {
          this.setState({
            uploadProgress: 100
          });
        } else {
          this.setState({
            uploadProgress: perc
          });
        }
      };
      // todo: parameterize this path
      xhr.open('POST', '/api/alltrails/convert', true);
      xhr.setRequestHeader('X-AT-KEY', this.props.apiKey);
      xhr.setRequestHeader('X-AT-AUTH-TOKEN', this.props.authToken);
      xhr.setRequestHeader('X-Language-Locale', this.props.languageRegionCode);
      xhr.setRequestHeader('X-CSRF-Token', document.querySelector('meta[name="csrf-token"]').content);
      xhr.onload = function () {
        if (this.status >= 200 && this.status < 300) {
          resolve(xhr.response);
        } else if (this.status == 400) {
          const parsed = JSON.parse(xhr.response);
          reject({
            status: this.status,
            localizedErrorMsg: parsed.localizedErrorMsg
          });
        } else {
          reject({
            status: this.status,
            statusText: xhr.statusText
          });
        }
      };
      xhr.onerror = function () {
        reject({
          status: this.status,
          statusText: xhr.statusText
        });
      };
      xhr.send(data);
    });
  }

  handleUploadComplete(filePath, map) {
    // check if the user is logged in. If not, send them to the login page.
    if (!this.props.loggedInUser && !this.props.mobileBrowser) {
      return (window.location = LanguageSupportUtil.wrapUrlSafe('/signup?returnTo=/converter/success', this.props.languageRegionCode));
    }
    // If the user is logged in, set uploadComplete to true to send them to the success page.
    if (map) {
      window.location = LanguageSupportUtil.wrapUrlSafe('/converter/success', this.props.languageRegionCode);
    } else {
      this.setState(state => {
        state.submitting = false;
        state.uploadComplete = true;
        state.files.uploadedPath = filePath;
      });
    }
  }

  formReset() {
    // This runs on form field change events (for clearing validation/error messages)
    this.handleError('');
  }

  handleError(message) {
    this.setState({
      submitting: false,
      errorMessage: message
    });
  }

  serializeFormData(formData) {
    const response = {};
    for (let i = 0; i < formData.length; i++) {
      response[formData[i].name] = formData[i].value;
    }
    return response;
  }

  handleSubmit(event) {
    event.preventDefault();
    this.formReset();
    this.setState({
      submitting: true
    });

    if (!this.state.files.isAttached && !this.state.files.fileUrl) {
      this.handleError(StringConstantsMixin.strings.CONVERTER.ERROR_MESSAGES.NO_FILE_ATTACHED);
      return;
    }

    if (this.state.files.fileSize > this.state.fileSizeLimit) {
      // todo: use displayFileSize function to format this dynamically
      // todo: perform file size validation when attaching, not when submitting
      this.handleError(StringConstantsMixin.strings.CONVERTER.ERROR_MESSAGES.TOO_LARGE);
      return;
    }

    const formData = new FormData(event.target);
    formData.append('key', this.props.apiKey);
    if (this.state.files.isAttached) {
      formData.append('formFile', this.state.files.attachedFile);
    }

    const context = this;

    this.sendData(formData)
      .then(response => {
        const parsed = JSON.parse(response);
        this.handleUploadComplete(parsed.s3_path, parsed.map);
      })
      .catch(function (err) {
        // Some kind of error happened on upload
        context.handleError(err.localizedErrorMsg ? err.localizedErrorMsg : StringConstantsMixin.strings.CONVERTER.ERROR_MESSAGES.UPLOAD_FAILED);
      });
  }

  toggleRouteOption(optionKey) {
    return e => {
      const stateItem = !!this.state.routeOptions.options[optionKey].enabled;
      const routeOptions = this.state.routeOptions;
      routeOptions.options[optionKey].enabled = !stateItem;
      this.setState({ routeOptions });
    };
  }

  toggleCueSheetOptions() {
    const context = this;
    const cueSheetState = this.state.cueSheets;
    cueSheetState.on = !cueSheetState.on;
    context.setState({ cueSheets: cueSheetState });
  }

  selectTrackType(selected) {
    this.setState({
      convertTo: selected
    });
  }

  selectSimplificationOption(value) {
    const currentSimplification = this.state.routeSimplification;
    currentSimplification.selectedOption = value;
    this.setState({ routeSimplification: currentSimplification });
  }

  toggleExpanded(key) {
    const current = this.state[key];
    current.expanded = !current.expanded;
    const newState = {};
    newState[key] = current;
    this.setState(newState);
  }

  getFileSize() {
    // Limit FileSize
    let { fileSize } = this.state.files;
    if (fileSize === 0) {
      return 0;
    }
    // todo: make much more robust file size component
    fileSize = (fileSize / 1000).toFixed(1);
    return `${fileSize} kb`;
  }

  getRouteSimplificationOptions() {
    const options = this.props.routeSimplification;
    return options.map(option => ({
      text: StringConstantsMixin.strings.CONVERTER.ROUTE_SIMPLIFICATION_STRINGS[option.stringKey],
      selected: option.value === this.state.routeSimplification.selectedOption,
      value: option.value
    }));
  }

  getFileTypeOptions() {
    const options = this.state.fileTypes;
    return options.map(fileType => ({
      text: fileType.display,
      selected: fileType.value === this.state.convertTo,
      value: fileType.value
    }));
  }

  render() {
    // Get translated strings from props to pass to fileUpload
    const fileUploadStrings = {
      dragSupportedFiles: StringConstantsMixin.strings.CONVERTER.DRAG_SUPPORTED,
      orChooseFile: StringConstantsMixin.strings.CONVERTER.OR_CHOOSE_FILE,
      fileSizeText: StringConstantsMixin.strings.CONVERTER.FILE_SIZE,
      removeFileText: StringConstantsMixin.strings.CONVERTER.REMOVE_FILE
    };
    const fileUploadSection = (
      <div className="panel-body">
        <DragAndDrop disabled={this.state.files.fileUrl} handleDrop={this.handleDrop().bind(this)}>
          <FileUpload
            key={this.state.files.key}
            handleChange={this.handleFileFieldChange().bind(this)}
            handleRemove={this.handleRemove().bind(this)}
            strings={fileUploadStrings}
            fileName={this.state.files.fileName}
            fileSize={this.getFileSize()}
            fileAttached={this.state.files.isAttached}
          />
        </DragAndDrop>
        <SectionDivider>{StringConstantsMixin.strings.CONVERTER.OR}</SectionDivider>
        <TextInput
          disabled={this.state.files.isAttached}
          name="formFileUrl"
          value={this.state.files.fileUrl}
          placeholder={StringConstantsMixin.strings.CONVERTER.FILE_URL_PLACEHOLDER}
          onChange={this.stateHandler('files', 'fileUrl').bind(this)}
          handleClear={this.resetField('files', 'fileUrl').bind(this)}
        />
      </div>
    );

    const bodyContent = (
      <form onSubmit={this.handleSubmit.bind(this)}>
        <input type="hidden" name="metric" value={this.props.metric} />
        <div className="container">
          <div className="section">
            <h5>{StringConstantsMixin.strings.CONVERTER.UPLOAD_FILE}</h5>
            <div className="panel-body">
              <p className="body1">{StringConstantsMixin.strings.CONVERTER.UPLOAD_FILE_PROMPT}</p>
              <p className="body2">
                <FormattedMessage
                  defaultMessage="View <a>Supported File Formats</a>"
                  values={{
                    a: chunks => (
                      <a href={this.props.supportUrl} target="_blank" rel="noreferrer">
                        {chunks}
                      </a>
                    )
                  }}
                />
              </p>
              <div className="panel-body">{fileUploadSection}</div>
            </div>
          </div>
          <div className="section">
            <div className="section-head">
              <h5 className="subtitle1">{StringConstantsMixin.strings.CONVERTER.CONVERT_TO}</h5>
            </div>
            <div className="panel-body">
              <RadioGroup options={this.getFileTypeOptions()} onChange={this.selectTrackType.bind(this)} testId="converter-radio-group" />
              <p className="body1">{StringConstantsMixin.strings.CONVERTER.DOWNLOAD_AS}</p>
              <Select
                name="filetype"
                value={this.state.saveAs}
                options={this.props.fileTypes[this.state.convertTo]}
                handleSelect={this.updateSaveAs}
              />
            </div>
          </div>
          <div className="section">
            <div className="section-head">
              <h5 className="subtitle1">{StringConstantsMixin.strings.CONVERTER.CUSTOMIZE_ROUTE}</h5>
            </div>
            <div>
              <Collapsible
                title={StringConstantsMixin.strings.CONVERTER.ROUTE_OPTIONS}
                expanded={this.state.routeOptions.expanded}
                onClick={() => this.toggleExpanded('routeOptions')}
              >
                <div className="panel-body">
                  <CheckboxGroup
                    strings={StringConstantsMixin.strings.CONVERTER.ROUTE_OPTION_STRINGS}
                    options={this.state.routeOptions.options}
                    onClick={this.toggleRouteOption.bind(this)}
                  />
                </div>
              </Collapsible>
            </div>
            <div>
              <Collapsible
                title={StringConstantsMixin.strings.CONVERTER.ROUTE_SIMPLIFICATION}
                expanded={this.state.routeSimplification.expanded}
                onClick={() => this.toggleExpanded('routeSimplification')}
                id="rt-simplification"
              >
                <RadioGroup
                  strings={StringConstantsMixin.strings.CONVERTER.ROUTE_SIMPLIFICATION_STRINGS}
                  options={this.getRouteSimplificationOptions()}
                  selected={this.state.routeSimplification.selectedOption}
                  onChange={this.selectSimplificationOption.bind(this)}
                />
                <div className={styles.sliderContainer}>
                  <SliderGroup
                    label={
                      <span>
                        <b>
                          <FormattedMessage defaultMessage="{value} pts" values={{ value: this.state.routeSimplification.value }} />
                        </b>
                      </span>
                    }
                    min="1"
                    disabled={this.state.routeSimplification.selectedOption != 'custom'}
                    max={this.props.routeSimplification[this.props.routeSimplification.length - 1].max}
                    name="maxTrackpoints"
                    onChange={this.stateHandler('routeSimplification', 'value').bind(this)}
                    value={this.state.routeSimplification.value}
                  />
                </div>
              </Collapsible>
            </div>
            <div>
              <Collapsible
                title={StringConstantsMixin.strings.CONVERTER.CUE_SHEETS}
                expanded={this.state.cueSheets.expanded}
                onClick={() => this.toggleExpanded('cueSheets')}
              >
                <div className="panel-body">
                  <div>
                    <div className="form-prompt">
                      <p className="body3">{StringConstantsMixin.strings.CONVERTER.CUE_SHEETS_PROMPT}</p>
                    </div>
                    <div>
                      <Switch
                        className="form-group toggle-group"
                        labelText={this.state.cueSheets.on ? StringConstantsMixin.strings.CONVERTER.ON : StringConstantsMixin.strings.CONVERTER.OFF}
                        selected={this.state.cueSheets.on}
                        testId="toggleCueSheetSwitch"
                        onChange={this.toggleCueSheetOptions.bind(this)}
                      />
                      <input name="toggleCueSheets" value={this.state.cueSheets.on ? 'on' : 'off'} type="hidden" />
                    </div>
                  </div>
                  <div className="form-group">
                    <SliderGroup
                      label={
                        <span>
                          {StringConstantsMixin.strings.CONVERTER.NUMBER_OF_CUES} <b>{this.state.cueSheets.number}</b>
                        </span>
                      }
                      min="1"
                      disabled={!this.state.cueSheets.on}
                      max={this.props.cueSheetOptions.number.range}
                      name={this.props.cueSheetOptions.number.name}
                      onChange={this.stateHandler('cueSheets', 'number').bind(this)}
                      value={this.state.cueSheets.number}
                    />
                  </div>
                  <div className="form-group">
                    <SliderGroup
                      label={
                        <span>
                          {StringConstantsMixin.strings.CONVERTER.MOVE_CUE_LEAD}{' '}
                          <b>
                            {this.state.cueSheets.distance} {this.props.cueSheetOptions.distance.units}
                          </b>{' '}
                          {StringConstantsMixin.strings.CONVERTER.MOVE_CUE_TAIL}
                        </span>
                      }
                      min="1"
                      disabled={!this.state.cueSheets.on}
                      max={this.props.cueSheetOptions.distance.range}
                      name={this.props.cueSheetOptions.distance.name}
                      onChange={this.stateHandler('cueSheets', 'distance').bind(this)}
                      value={this.state.cueSheets.distance}
                    />
                  </div>
                </div>
              </Collapsible>
            </div>
          </div>
          <div className={styles.foot}>
            <Button
              loading={this.state.submitting}
              text={StringConstantsMixin.strings.CONVERTER.CONVERT_FILE}
              testId="converter-submit"
              type="submit"
              variant="primary"
            />
            <div className="errorMessage">{this.state.errorMessage}</div>
            <div className="body3">
              <p>{StringConstantsMixin.strings.CONVERTER.SUPPORTED_FORMATS_LEAD}</p>
              <p className="xlate-none">
                Google Earth (KML, KMZ), Google Maps directions (XML, JSON), PCX5 (tracks, waypoints), GPX (tracks, routes, waypoints), GPX Garmin
                Streetpilot, Garmin Course (CRS, TCX), FIT (ANT+), MS Excel, CSV (Comma-Separated-Values), Falk IBEX Tour, CompeGPS, VDO GP7 (TRC),
                GeoRSS, Logbook, NMEA, OVL (ASCII), Fugawi, KOMPASS Verlag (Alpenverein), TrainingPeaks (PWX), Navigon Route, OziExplorer, qpeGps
                Track, MagicMaps IKT, TomTom BIN (ttbin), TomTom ITN, Suunto SDF, Magellan Track, PathAway
              </p>
            </div>
          </div>
        </div>
      </form>
    );

    return (
      <CustomProvider>
        <div className="wrapper">
          <div className="head">
            <div className="hero converter-hero">
              <h1 className="text-center">{StringConstantsMixin.strings.CONVERTER.HERO_TITLE}</h1>
            </div>
          </div>
          {bodyContent}
        </div>
      </CustomProvider>
    );
  }
}

export { Converter };
